import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import {
  createSettingItem,
  deleteSettingItem,
  fetchSettings,
  selectSettingsGroup,
  updateSettingItem,
} from '@main/features/settings/settingsSlice';
import { UiButton, UiInput, UiTextarea } from '@shared/common/ui';

/**
 * Простой словарь: список записей со структурой value = { name, color?, description? }.
 * Подходит для client_statuses, order_statuses, expenditure.
 */
export function DictSettings({ group, title, help }) {
  const dispatch = useDispatch();
  const { items, status, saveStatus, saveError } = useSelector(selectSettingsGroup(group));
  const [editingKey, setEditingKey] = useState(null);

  useEffect(() => {
    if (status === 'idle') dispatch(fetchSettings(group));
  }, [dispatch, group, status]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { name: '', color: '#3a6df0', description: '' } });

  const onSubmit = async (values) => {
    const value = {
      name: values.name.trim(),
      color: values.color,
      description: values.description?.trim() || null,
    };
    const thunk = editingKey
      ? updateSettingItem({ group, key: editingKey, value })
      : createSettingItem({ group, value });
    const result = await dispatch(thunk);
    if (result.meta.requestStatus === 'fulfilled') {
      reset({ name: '', color: '#3a6df0', description: '' });
      setEditingKey(null);
    }
  };

  const onEdit = (item) => {
    setEditingKey(item.key);
    reset({
      name: item.value?.name ?? '',
      color: item.value?.color ?? '#3a6df0',
      description: item.value?.description ?? '',
    });
  };

  const onCancel = () => {
    setEditingKey(null);
    reset({ name: '', color: '#3a6df0', description: '' });
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Удалить «${item.value?.name ?? item.key}»?`)) return;
    await dispatch(deleteSettingItem({ group, key: item.key }));
  };

  return (
    <div className="form-grid" style={{ alignItems: 'start' }}>
      <div className="form-card">
        <h2>{editingKey ? 'Редактирование' : 'Добавить запись'}</h2>
        {help && <p className="muted">{help}</p>}

        <form onSubmit={handleSubmit(onSubmit)}>
          <UiInput
            label="Название"
            type="text"
            error={errors.name?.message}
            {...register('name', {
              required: 'Введите название',
              maxLength: { value: 255, message: 'Слишком длинное название' },
            })}
          />
          <UiInput
            label="Цвет"
            type="color"
            style={{ maxWidth: 120 }}
            {...register('color')}
          />
          <UiTextarea
            label="Описание"
            rows={3}
            error={errors.description?.message}
            {...register('description', {
              maxLength: { value: 500, message: 'Не больше 500 символов' },
            })}
          />

          {saveError && <div className="banner banner--danger">{saveError}</div>}

          <div className="form-actions">
            {editingKey && (
              <UiButton variant="ghost" type="button"  onClick={onCancel}>
                Отмена
              </UiButton>
            )}
            <UiButton
              type="submit"
              
              disabled={isSubmitting || saveStatus === 'loading'}
            >
              {editingKey ? 'Сохранить' : 'Добавить'}
            </UiButton>
          </div>
        </form>
      </div>

      <div>
        <h2 style={{ marginTop: 0 }}>{title}</h2>
        {items.length === 0 ? (
          <div className="placeholder">Пока пусто</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Описание</th>
                <th style={{ width: 160 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.key}>
                  <td>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        background: item.value?.color ?? '#ccc',
                        marginRight: 8,
                      }}
                    />
                    {item.value?.name ?? '—'}
                  </td>
                  <td className="muted">{item.value?.description ?? ''}</td>
                  <td>
                    <div className="row-actions">
                      <UiButton variant="ghost" size="sm"
                        type="button"
                        
                        onClick={() => onEdit(item)}
                      >
                        Изменить
                      </UiButton>
                      <UiButton variant="danger" size="sm"
                        type="button"
                        
                        onClick={() => onDelete(item)}
                      >
                        Удалить
                      </UiButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
